// composer/CaptionReviewDialog.tsx — correct automatic captions before posting (1.27.60). Each
// line's words are editable; a line can be removed. Timings stay as the speech model heard them.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, IconButton } from '@/ui';
import type { Cue } from '@/features/videos/captions/vtt';
import { formatClock } from '@/features/videos/player/chapters';

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
  max-height: min(60vh, 520px);
  overflow-y: auto;
  width: 100%;
`;

const Line = styled.li`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  time {
    padding-top: 8px;
    color: ${({ theme }) => theme.color.accentCyan};
    font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    font-variant-numeric: tabular-nums;
  }
  textarea {
    width: 100%;
    min-height: 38px;
    padding: 7px 10px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    resize: vertical;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

export function CaptionReviewDialog({
  cues,
  onSave,
  onClose,
}: {
  cues: Cue[];
  onSave: (cues: Cue[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(cues);
  return (
    <Dialog open onClose={onClose} title="Review captions">
      <List aria-label="Caption lines">
        {draft.map((c, i) => (
          <Line key={`${c.start}-${i}`}>
            <time>{formatClock(c.start)}</time>
            <textarea
              aria-label={`Caption at ${formatClock(c.start)}`}
              value={c.text}
              onChange={(e) =>
                setDraft((d) => d.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))
              }
            />
            <IconButton
              icon="trash"
              size={16}
              label={`Remove the caption at ${formatClock(c.start)}`}
              onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
            />
          </Line>
        ))}
      </List>
      <Footer>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSave(draft.filter((c) => c.text.trim()));
            onClose();
          }}
        >
          Save
        </Button>
      </Footer>
    </Dialog>
  );
}
